/**
 * Automation Workflows — ONE list for everything.
 *
 * Two kinds of workflow share the schema; instead of two tabs they live in a single
 * table with a Direction column (filterable):
 *  - Incoming: `message.received` flows — a stack that runs top-to-bottom per
 *    incoming WhatsApp message (first active keyword match wins), with the always-on
 *    Default AI tier pinned read-only underneath (the "resolution path" pattern).
 *    These rows carry a priority number + move up/down arrows.
 *  - Outgoing: lead / appointment / campaign-event automations.
 * Priority only means something for the incoming stack, so the arrows are offered
 * only while the incoming list is shown whole (no search / status filter applied).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Switch,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  alpha,
} from '@mui/material';
import {
  Add,
  DeleteOutline,
  ChevronRight,
  ArrowUpward,
  ArrowDownward,
  AutoAwesome,
  Bolt,
  TouchApp,
  Search,
  CallReceived,
  CallMade,
  ForumOutlined,
  AccountTreeOutlined,
  History,
} from '@mui/icons-material';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import adminApi from '@/services/admin.api';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { usePlanFeature } from '@/hooks/usePlanFeature';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Workflow {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  priority?: number;
  trigger: { type: string; config: Record<string, any> };
  nodes: any[];
  edges: any[];
  stats: {
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    lastExecutedAt?: string;
  };
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = {
  'lead.created': 'Incoming lead',
  'lead.updated': 'Lead updated',
  'lead.status_changed': 'Lead status changed',
  'lead.priority_changed': 'Lead priority changed',
  'appointment.created': 'Incoming appointment',
  'appointment.status_changed': 'Appointment status changed',
  'campaign.sent': 'Campaign sent',
  'campaign.failed': 'Campaign failed',
  'campaign.replied': 'Campaign replied',
  manual: 'Manual / API',
  'message.received': 'Incoming WhatsApp message',
};

const MESSAGE_TRIGGER = 'message.received';
const COLS = 'minmax(230px,2.2fr) 112px minmax(170px,1.8fr) 84px 124px 148px';

type Direction = 'all' | 'incoming' | 'outgoing';
type StatusFilter = 'all' | 'active' | 'draft';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isIncoming = (w: Workflow) => w.trigger?.type === MESSAGE_TRIGGER;

function fmtWhen(value?: string): string {
  if (!value) return 'Never run';
  const d = new Date(value);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Ran today';
  if (days === 1) return 'Ran yesterday';
  if (days < 7) return `Ran ${days} days ago`;
  return `Ran ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/** The keyword phrases a flow reacts to — gathered from its keyword nodes. */
function keywordsOf(w: Workflow): string[] {
  const out = new Set<string>();
  for (const n of w.nodes ?? []) {
    const kws = n?.data?.keywords;
    if (Array.isArray(kws)) kws.forEach((k) => typeof k === 'string' && k.trim() && out.add(k.trim()));
  }
  return [...out];
}

/** The button/list taps a flow branches on — gathered from its button-reply
 *  trigger nodes. Prefers the denormalized label the canvas picker stamps
 *  (`buttonLabel`), falling back to the raw id for a node saved before it. Keeps
 *  these distinct from keywords so the list can mark them as taps, not typed
 *  text — otherwise a button-only flow reads as the misleading "Any message". */
function buttonTapsOf(w: Workflow): string[] {
  const out = new Set<string>();
  for (const n of w.nodes ?? []) {
    if (n?.type !== 'button_reply_trigger') continue;
    const label = n?.data?.buttonLabel ?? n?.data?.buttonId;
    if (typeof label === 'string' && label.trim()) out.add(label.trim());
  }
  return [...out];
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function IconTile({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'primary' | 'amber' }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: (t) =>
          tone === 'primary' ? alpha(t.palette.primary.main, 0.1) : tone === 'amber' ? alpha(t.palette.warning.main, 0.14) : t.palette.action.hover,
        color: (t) => (tone === 'primary' ? t.palette.primary.main : tone === 'amber' ? t.palette.warning.dark : t.palette.text.secondary),
      }}
    >
      {children}
    </Box>
  );
}

function DirectionChip({ incoming }: { incoming: boolean }) {
  return (
    <Chip
      size="small"
      icon={incoming ? <CallReceived sx={{ fontSize: 14 }} /> : <CallMade sx={{ fontSize: 14 }} />}
      label={incoming ? 'Incoming' : 'Outgoing'}
      sx={{
        fontWeight: 600,
        fontSize: 12,
        bgcolor: (t) => alpha(incoming ? t.palette.primary.main : t.palette.warning.main, incoming ? 0.1 : 0.14),
        color: (t) => (incoming ? t.palette.primary.main : t.palette.warning.dark),
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

function Stat({ icon, value, of, label }: { icon: React.ReactNode; value: number; of?: number; label: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <IconTile>{icon}</IconTile>
      <Box>
        <Typography fontSize={22} fontWeight={700} lineHeight={1.1}>
          {value}
          {of !== undefined && (
            <Typography component="span" fontSize={15} color="text.secondary" fontWeight={500}>
              {' '}
              / {of}
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Workflows() {
  const navigate = useNavigate();
  const { hasFeature } = usePlanFeature();

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const [query, setQuery] = useState('');
  const [direction, setDirection] = useState<Direction>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDirection, setNewDirection] = useState<'incoming' | 'outgoing'>('incoming');

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadWorkflows = useCallback(async () => {
    try {
      setError(null);
      setWorkflows(await adminApi.getWorkflows());
    } catch (err: any) {
      setError(err.message ?? 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkflows();
  }, [loadWorkflows]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const incoming = useMemo(() => workflows.filter(isIncoming), [workflows]);
  const outgoing = useMemo(() => workflows.filter((w) => !isIncoming(w)), [workflows]);
  const activeCount = workflows.filter((w) => w.isActive).length;

  /** Incoming first (in routing order), then outgoing — then the user's filters. */
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...incoming, ...outgoing].filter((w) => {
      if (direction === 'incoming' && !isIncoming(w)) return false;
      if (direction === 'outgoing' && isIncoming(w)) return false;
      if (status === 'active' && !w.isActive) return false;
      if (status === 'draft' && w.isActive) return false;
      if (!q) return true;
      const hay = [w.name, w.description, TRIGGER_LABELS[w.trigger?.type] ?? '', ...keywordsOf(w), ...buttonTapsOf(w)].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [incoming, outgoing, query, direction, status]);

  const filtered = query.trim() !== '' || status !== 'all';
  const canReorder = !filtered && direction !== 'outgoing';

  // ── Actions ───────────────────────────────────────────────────────────────

  const toggleActive = async (wf: Workflow) => {
    try {
      if (wf.isActive) await adminApi.deactivateWorkflow(wf._id);
      else await adminApi.activateWorkflow(wf._id);
      await loadWorkflows();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message ?? 'Failed to toggle workflow');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await adminApi.deleteWorkflow(deleteId);
      setDeleteId(null);
      await loadWorkflows();
    } catch (err: any) {
      setDeleteId(null);
      setError(err.message ?? 'Failed to delete workflow');
    }
  };

  const openCanvas = (id: string) => navigate(`/admin/automation/workflows/${id}`);
  const openExecutions = (id: string) => navigate(`/admin/automation/workflows/${id}/executions`);

  const create = () => {
    const params = new URLSearchParams();
    if (newDirection === 'incoming') params.set('trigger', MESSAGE_TRIGGER);
    if (newName.trim()) params.set('name', newName.trim());
    const qs = params.toString();
    setCreateOpen(false);
    setNewName('');
    navigate(`/admin/automation/workflows/new${qs ? `?${qs}` : ''}`);
  };

  /** Move an incoming flow within the routing stack. Optimistic, then reconciled. */
  const move = async (w: Workflow, dir: -1 | 1) => {
    const ids = incoming.map((f) => f._id);
    const i = ids.indexOf(w._id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const [moved] = ids.splice(i, 1);
    ids.splice(j, 0, moved!);
    setReordering(true);
    const priorityById = new Map(ids.map((id, k) => [id, k]));
    setWorkflows((prev) =>
      [...prev].sort((a, b) => {
        if (!isIncoming(a) || !isIncoming(b)) return 0;
        return (priorityById.get(a._id) ?? a.priority ?? 0) - (priorityById.get(b._id) ?? b.priority ?? 0);
      })
    );
    try {
      await adminApi.reorderWorkflows(ids);
    } catch (err: any) {
      setError(err.message ?? 'Failed to reorder');
    } finally {
      await loadWorkflows(); // reconcile (also undoes the optimistic move on failure)
      setReordering(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', display: 'flex', flexDirection: 'column', minHeight: { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 104px)', md: 'calc(100vh - 48px)', lg: 'calc(100vh - 64px)' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Automation
          </Typography>
          <Typography fontSize={32} fontWeight={700} lineHeight={1.15}>
            Workflows
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            Replies to incoming WhatsApp messages and automations triggered by leads, appointments and campaigns — all in one place.
          </Typography>
        </Box>
        <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}>
          New workflow
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap sx={{ py: 2.5, borderTop: 1, borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Stat icon={<Bolt fontSize="small" />} value={activeCount} of={workflows.length} label="Active workflows" />
        <Stat icon={<CallReceived fontSize="small" />} value={incoming.length} label="Incoming" />
        <Stat icon={<CallMade fontSize="small" />} value={outgoing.length} label="Outgoing" />
      </Stack>

      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search workflows…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
        />
        <TextField select size="small" value={direction} onChange={(e) => setDirection(e.target.value as Direction)} sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
          <MenuItem value="all">All directions</MenuItem>
          <MenuItem value="incoming">Incoming</MenuItem>
          <MenuItem value="outgoing">Outgoing</MenuItem>
        </TextField>
        <TextField select size="small" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {rows.length} {rows.length === 1 ? 'workflow' : 'workflows'}
        </Typography>
      </Stack>

      {/* Table */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper', overflow: 'auto' }}>
        <Box sx={{ minWidth: 940 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: COLS, columnGap: 2, px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
            {['WORKFLOW', 'DIRECTION', 'KEYWORDS / EVENT', 'HANDLED', 'STATUS', ''].map((h) => (
              <Typography key={h || 'actions'} sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: 'text.secondary' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 7, textAlign: 'center' }}>
              <AccountTreeOutlined sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography color="text.secondary">
                {workflows.length === 0 ? 'No workflows yet — every message goes straight to the Default AI.' : 'No workflows match these filters.'}
              </Typography>
              {workflows.length === 0 ? (
                <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none' }}>
                  Create your first workflow
                </Button>
              ) : (
                <Button onClick={() => { setQuery(''); setStatus('all'); setDirection('all'); }} sx={{ textTransform: 'none' }}>
                  Clear filters
                </Button>
              )}
            </Stack>
          ) : (
            rows.map((w, idx) => {
              const inc = isIncoming(w);
              // Keyword and button-tap branches share this column — a flow can
              // have either or both. Keywords first, then taps (visually marked),
              // so "Any message" shows only when a flow truly branches on neither.
              const triggerChips = inc
                ? [
                    ...keywordsOf(w).map((label) => ({ label, tap: false })),
                    ...buttonTapsOf(w).map((label) => ({ label, tap: true })),
                  ]
                : [];
              const pos = inc ? incoming.findIndex((f) => f._id === w._id) : -1;
              return (
                <Box
                  key={w._id}
                  onClick={() => openCanvas(w._id)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    columnGap: 2,
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    borderTop: idx === 0 ? 0 : 1,
                    borderColor: 'divider',
                    opacity: w.isActive ? 1 : 0.7,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <IconTile tone={inc ? 'primary' : 'amber'}>{inc ? <ForumOutlined fontSize="small" /> : <AccountTreeOutlined fontSize="small" />}</IconTile>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>
                        {w.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {w.nodes?.length ?? 0} steps · {fmtWhen(w.stats?.lastExecutedAt)}
                        {(w.stats?.failedExecutions ?? 0) > 0 && (
                          <Box component="span" sx={{ color: 'error.main' }}> · {w.stats.failedExecutions} failed</Box>
                        )}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box>
                    <DirectionChip incoming={inc} />
                  </Box>

                  <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
                    {inc ? (
                      triggerChips.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Any message</Typography>
                      ) : (
                        <>
                          {triggerChips.slice(0, 3).map((c) => (
                            <Box
                              key={`${c.tap ? 'tap' : 'kw'}:${c.label}`}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 12, px: 1, py: 0.25, borderRadius: '6px', border: 1, borderColor: 'divider', bgcolor: 'action.hover', maxWidth: 130, overflow: 'hidden' }}
                            >
                              {c.tap && <TouchApp sx={{ fontSize: 13, flexShrink: 0, color: 'text.secondary' }} />}
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.label}
                              </Box>
                            </Box>
                          ))}
                          {triggerChips.length > 3 && <Typography variant="caption" color="text.secondary">+{triggerChips.length - 3}</Typography>}
                        </>
                      )
                    ) : (
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                        <Bolt sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{TRIGGER_LABELS[w.trigger?.type] ?? w.trigger?.type}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Typography fontWeight={500}>{w.stats?.totalExecutions ?? 0}</Typography>

                  <Stack direction="row" spacing={0.75} alignItems="center" onClick={(e) => e.stopPropagation()}>
                    <Switch size="small" checked={w.isActive} onChange={() => toggleActive(w)} inputProps={{ 'aria-label': `${w.isActive ? 'Deactivate' : 'Activate'} ${w.name}` }} />
                    <Typography variant="caption" color="text.secondary">{w.isActive ? 'Active' : 'Draft'}</Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                    {inc && canReorder && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, minWidth: 12, textAlign: 'right' }}>{pos + 1}</Typography>
                        <Stack>
                          <Tooltip title="Higher priority">
                            <span>
                              <IconButton size="small" sx={{ p: 0 }} disabled={pos <= 0 || reordering} onClick={() => move(w, -1)} aria-label="Move up">
                                <ArrowUpward sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Lower priority">
                            <span>
                              <IconButton size="small" sx={{ p: 0 }} disabled={pos >= incoming.length - 1 || reordering} onClick={() => move(w, 1)} aria-label="Move down">
                                <ArrowDownward sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </>
                    )}
                    <Tooltip title="Run history">
                      <IconButton size="small" onClick={() => openExecutions(w._id)} aria-label="Run history">
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteId(w._id)} aria-label="Delete workflow">
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => openCanvas(w._id)} aria-label="Open in editor">
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, px: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {workflows.length} {workflows.length === 1 ? 'workflow' : 'workflows'} in this workspace
        </Typography>
        {canReorder && incoming.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            Incoming flows run in priority order — the first active keyword match wins
          </Typography>
        )}
      </Stack>

      {/* Always-on default tier — pinned, read-only, un-deletable. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 3, p: 2, borderRadius: '12px', border: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <IconTile>
          <AutoAwesome fontSize="small" />
        </IconTile>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600}>Default AI response</Typography>
          <Typography variant="caption" color="text.secondary">
            Fallback for unmatched incoming messages · picks the best agent · escalates to a human on your global keywords or low confidence
          </Typography>
        </Box>
        <Chip label="Always last" size="small" variant="outlined" />
      </Stack>

      {/* Footer — always at the bottom of the page, however few workflows there are. */}
      <Box sx={{ mt: 'auto', pt: 5, display: 'flex', justifyContent: 'flex-end' }}>
        {hasFeature('httpAutomationEnabled') && (
          <Link component="button" type="button" underline="hover" onClick={() => navigate('/admin/automation/api-connections')} sx={{ fontSize: 14 }}>
            API connections →
          </Link>
        )}
      </Box>

      {/* New workflow */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New workflow</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Workflow name"
              placeholder="e.g. Appointment follow-up"
              size="small"
              fullWidth
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              select
              label="Direction"
              size="small"
              fullWidth
              value={newDirection}
              onChange={(e) => setNewDirection(e.target.value as 'incoming' | 'outgoing')}
              helperText={
                newDirection === 'incoming'
                  ? 'Runs when a customer sends a WhatsApp message that matches your keywords.'
                  : 'Runs when something happens — a new lead, an appointment, a campaign reply.'
              }
            >
              <MenuItem value="incoming">Incoming — reply to messages</MenuItem>
              <MenuItem value="outgoing">Outgoing — react to events</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={create} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Create workflow
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteId !== null}
        title="Delete Workflow"
        content="This will permanently delete this workflow. Existing execution logs will be preserved."
        confirmText="Delete"
        severity="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
